import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import packageInfo from 'package.json';
import { take } from 'rxjs/operators';
import { UpdateAvailableDialogComponent } from '../shared/version-update/update-available-dialog.component';
import { AppVersionService } from './app-version.service';

@Injectable({
  providedIn: 'root',
})
export class VersionUpdateService implements OnDestroy {
  private static readonly CHECK_INTERVAL = 30_000;
  private static readonly LOCAL_VERSION_STORAGE_KEY = 'ecs-app-version';
  private static readonly REMOTE_VERSION_STORAGE_KEY = 'ecs-app-remote-version';

  private checkIntervalId?: number;
  private dialogRef?: MatDialogRef<UpdateAvailableDialogComponent, 'reload'>;
  private lastPromptedVersion?: string;
  private readonly currentVersion = packageInfo.version;
  private readonly storageListener = (event: StorageEvent) => this.onStorageEvent(event);

  constructor(
    private readonly dialog: MatDialog,
    private readonly ngZone: NgZone,
    private readonly appVersionService: AppVersionService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  public start(): void {
    if (this.checkIntervalId || typeof window === 'undefined') {
      return;
    }

    this.persistCurrentVersion();
    this.checkForUpdates();

    window.addEventListener('storage', this.storageListener);
    this.checkIntervalId = window.setInterval(() => this.checkForUpdates(), VersionUpdateService.CHECK_INTERVAL);
  }

  public stop(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.storageListener);
    }

    if (this.checkIntervalId) {
      window.clearInterval(this.checkIntervalId);
      this.checkIntervalId = undefined;
    }
  }

  private onStorageEvent(event: StorageEvent): void {
    if (event.key !== VersionUpdateService.REMOTE_VERSION_STORAGE_KEY) {
      return;
    }

    this.handleRemoteVersion(event.newValue ?? '');
  }

  private checkForUpdates(): void {
    this.appVersionService
      .fetchLatestVersion({ bustCache: true })
      .pipe(take(1))
      .subscribe((remoteVersion) => this.handleRemoteVersion(remoteVersion));
  }

  private handleRemoteVersion(remoteVersion: string): void {
    if (!remoteVersion) {
      return;
    }

    this.persistRemoteVersion(remoteVersion);

    const cachedCurrentVersion = this.getCachedCurrentVersion();

    if (!cachedCurrentVersion) {
      this.persistCurrentVersion();
      return;
    }

    if (remoteVersion === cachedCurrentVersion) {
      return;
    }

    if (this.dialogRef || this.lastPromptedVersion === remoteVersion) {
      return;
    }

    this.openDialog(remoteVersion);
  }

  private openDialog(version: string): void {
    this.ngZone.run(() => {
      this.dialogRef = this.dialog.open(UpdateAvailableDialogComponent, {
        width: '420px',
        disableClose: true,
        data: {
          version,
        },
      });

      this.lastPromptedVersion = version;

      this.dialogRef.afterClosed().subscribe((result) => {
        this.dialogRef = undefined;

        if (result === 'reload') {
          this.document.defaultView?.location.reload();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private persistCurrentVersion(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(VersionUpdateService.LOCAL_VERSION_STORAGE_KEY, this.currentVersion);
    } catch (error) {
      console.warn('Unable to persist current app version', error);
    }
  }

  private getCachedCurrentVersion(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window.localStorage.getItem(VersionUpdateService.LOCAL_VERSION_STORAGE_KEY);
    } catch (error) {
      console.warn('Unable to read cached app version', error);
      return null;
    }
  }

  private persistRemoteVersion(version: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(VersionUpdateService.REMOTE_VERSION_STORAGE_KEY, version);
    } catch (error) {
      console.warn('Unable to persist remote app version', error);
    }
  }
}
